import { getChordNotes, voiceChord } from './musicTheory';
import { Capacitor } from '@capacitor/core';
import * as Tone from 'tone';
import { createMixBus } from './playbackMix';
import { connectReverbSend, whenReverbReady, setReverbDecay as setSharedReverbDecay } from './reverbBus';
import { REVERB_DECAY, REVERB_MIX, savedReverbMix, savedDegreeGains } from './audioSettings';
import { subscribeUser } from '../data/userStore';
import { recordPreloadTiming } from './devicePerformance';

// Les loops sont nommees en dieses (audio/notes/C#3.flac). getNotesInMode rend
// l'orthographe musicale de la gamme (Bb, Cb, E#...), qui ne correspond pas
// toujours au nom de fichier : on la ramene ici sur ce nommage.
const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const NOTE_OCTAVES = [2, 3, 4];
const PITCH_CLASSES = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

const toFileNoteName = (name) => {
  if (typeof name !== 'string' || !name.length) return null;
  const letter = name[0].toUpperCase();
  if (!(letter in PITCH_CLASSES)) return null;
  let index = PITCH_CLASSES[letter];
  for (const accidental of name.slice(1)) {
    if (accidental === '#' || accidental === '♯') index += 1;
    else if (accidental === 'b' || accidental === 'B' || accidental === '♭') index -= 1;
  }
  return NOTE_NAMES[((index % 12) + 12) % 12];
};

/**
 * Multi-Note Player
 *
 * Plays individual high-quality WAV files simultaneously to create chords.
 * Uses Web Audio API for sample-accurate mixing with zero glitches on mobile.
 *
 * Features:
 * - Loads 36 pre-generated WAV files (12 notes × 3 octaves)
 * - Mixes notes in real-time using Web Audio API
 * - Sample-accurate timing for glitch-free playback
 * - Optimized for mobile devices
 */
class MultiNotePlayer {
  constructor() {
    this.audioContext = null;
    this.noteBuffers = new Map(); // 'C2' -> AudioBuffer
    this.currentSources = []; // Currently playing sources
    this.currentGains = []; // Gain nodes for current sources (for crossfade)
    this.masterGain = null;
    this.reverbSend = null; // Envoi de la nappe vers l'unique reverb (cf reverbBus)
    this.initialized = false;
    this.isPreloading = false;
    // Travaux en vol, partages entre appels concurrents (cf initialize).
    this._initializing = null;
    this._preloading = null;
    this.playHighOctave = true;
    this.volume = -6; // dB
    // Per-degree linear gains applied before the master gain.
    // Drone bass is boosted by default for presence; chord tones are unity.
    this.degreeGains = { drone: 1.8, root: 1.0, third: 1.0, fifth: 1.0, seventh: 1.0 };
    this.reverbMix = REVERB_MIX; // Default reverb wet mix (0-1)
    this.reverbDecay = REVERB_DECAY; // Duree commune a toute l'app (cf reverbBus)
    this._reverbDecayTimeout = null; // Debounce timer for decay changes
    this.gestureUnlocked = false;
    this.unlockHandlerInstalled = false;
    this.silentAudio = null;
    this.crossfadeDuration = 0.3; // Crossfade time in seconds (300ms)
    // The outgoing chord starts fading before the beat, so the harmony has already
    // half-swapped when the beat lands instead of swapping entirely after it.
    // Measured: the old third was still at 50% 31ms past the beat; leading by 80ms
    // brings that to ~9ms, and stays well inside the Transport's scheduling lead
    // (the callback fires 110 to 180ms before the beat it carries).
    this.crossfadeLead = 0.08;
    this.fadeInDuration = 0.02; // Initial fade-in time in seconds (20ms)
    // Les reglages du mixer n'arrivent parfois qu'apres le demarrage :
    // hydratation du miroir user, puis synchro du compte a la connexion. On les
    // repousse a chaque notification du miroir, comme audioEngine le fait pour
    // le timbre, pour ne plus dependre de l'ouverture de la page You.
    subscribeUser(() => this.applySavedMix());
  }

  // Reglages du mixer sauvegardes -> moteur. Une valeur identique ne touche a
  // rien : pendant un glissement de curseur, AudioMixer a deja applique la
  // valeur en direct avant de l'ecrire.
  applySavedMix() {
    const mix = savedReverbMix();
    if (mix !== this.reverbMix) this.setReverbMix(mix);
    const gains = savedDegreeGains();
    for (const degree of Object.keys(gains)) {
      if (gains[degree] !== this.degreeGains[degree]) this.setDegreeVolume(degree, gains[degree]);
    }
  }

  // Create a silent HTMLAudioElement to help unlock iOS audio policies
  createSilentAudio() {
    if (this.silentAudio) return this.silentAudio;
    try {
      const audio = document.createElement('audio');
      audio.src = 'data:audio/mp3;base64,//uQxAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAACcQCAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICA//////////////////////////////////////////////////////////////////8AAABhTEFNRTMuMTAwA8MAAAAAAAAAABQgJAUHQQAB9AAAAnGMHkkIAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA//sQxAADgnABGiAAQBCqgCRMAAgEAH///////////////7+n/9FTuQsQH//////2NG0jWUGlio5gLQTOtIoeR2WX////X4s9Atb/JRVCbBUpeRUq//////////////////9RUi0f2jn/+xDECgPCjAEQAABN4AAANIAAAAQVTEFNRTMuMTAwVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVQ==';
      audio.loop = false;
      audio.autoplay = false;
      audio.preload = 'auto';
      audio.style.display = 'none';
      document.body.appendChild(audio);
      this.silentAudio = audio;
    } catch (_) {}
    return this.silentAudio;
  }

  // Install one-time gesture listeners to unlock Web Audio on iOS WKWebView
  setupGestureUnlock() {
    if (this.unlockHandlerInstalled) return;
    const handler = async () => {
      await this.unlock();
      window.removeEventListener('touchend', handler, true);
      window.removeEventListener('pointerdown', handler, true);
      window.removeEventListener('mousedown', handler, true);
    };
    try {
      window.addEventListener('touchend', handler, true);
      window.addEventListener('pointerdown', handler, true);
      window.addEventListener('mousedown', handler, true);
      this.unlockHandlerInstalled = true;
    } catch (_) {}
  }

  // Perform the actual unlock: play a tiny audio and resume our AudioContext
  async unlock() {
    if (!this.audioContext) return;
    if (this.gestureUnlocked) return;
    try {
      const silent = this.createSilentAudio();
      if (silent) {
        try { await silent.play(); } catch (_) {}
      }
      try { await this.audioContext.resume(); } catch (_) {}
      this.gestureUnlocked = true;
    } catch (e) {
      console.warn('[MultiNotePlayer] Gesture unlock failed:', e);
    }
  }

  /**
   * Initialize the audio context with optimal settings for quality
   */
  async initialize() {
    if (this.initialized) return;
    // Deux appels concurrents doivent partager le meme travail. L'effet de
    // montage d'App.js part deux fois en developpement (StrictMode) : sans ce
    // verrou, les deux passaient la garde ci-dessus (`initialized` n'est pose
    // qu'a la fin) et batissaient chacun leur reverb, leur gain et leurs bus.
    if (!this._initializing) {
      this._initializing = this._runInitialize().finally(() => { this._initializing = null; });
    }
    return this._initializing;
  }

  async _runInitialize() {
    try {
      // CRITICAL iOS 17+ FIX: Set audio session type BEFORE creating AudioContext
      if (Capacitor.isNativePlatform() && navigator.audioSession) {
        try {
          navigator.audioSession.type = 'playback';
        } catch (e) {
          // Silently fail on older iOS versions
        }
      }

      // Use Tone.js's AudioContext to ensure perfect timing sync with metronome/progressions
      // This is CRITICAL for accurate playback timing when using Transport-based scheduling
      //
      // SUR LE WEB, ne jamais attendre `Tone.start()` ni le `resume()` ici :
      // tant qu'aucun geste n'a eu lieu, Chrome laisse la promesse de reprise EN
      // ATTENTE (il ne la rejette pas), et tout le prechargement restait
      // derriere. Il ne demarrait qu'au premier clic, si bien que les 36 loops
      // se decodaient pendant que l'utilisateur jouait. Le contexte, lui, se
      // cree sans geste et `decodeAudioData` marche sur un contexte suspendu :
      // le chargement complet peut donc se faire avant la premiere note. Seule
      // la sortie audio a besoin du geste, et c'est le role de
      // setupGestureUnlock plus bas.
      //
      // Sur natif, la WebView autorise la lecture sans geste : la reprise
      // aboutit des le lancement, elle n'a jamais rien bloque, et on garde donc
      // le chemin eprouve tel quel.
      const native = Capacitor.isNativePlatform();
      if (native) await Tone.start();
      else Tone.start().catch(() => {});
      this.audioContext = Tone.getContext().rawContext;

      // Resume audio context if suspended
      if (this.audioContext.state === 'suspended') {
        try {
          if (native) await this.audioContext.resume();
          else this.audioContext.resume().catch(() => {});
        } catch (e) {
          // Will be resumed on first user gesture
        }
      }

      // Create Tone.js Gain node for volume control (not native Web Audio API Gain)
      // This ensures compatibility when connecting to Tone.js effects
      this.masterGain = new Tone.Gain();
      this.setVolume(this.volume);
      this.mixBuses = {
        chords: createMixBus('chords').connect(this.masterGain),
        drone: createMixBus('drone').connect(this.masterGain),
      };

      // Chaine : bus de mix -> masterGain -> direct + envoi vers l'unique
      // reverb, laquelle rejoint le bus stereo commun, puis l'EQ global et la
      // sortie (cf reverbBus). L'envoi est pris apres le fader, donc le curseur
      // du mixer baisse aussi la queue. Le dosage et les volumes par degre sont
      // relus juste avant : le miroir user s'hydrate apres l'evaluation des
      // modules, la valeur du constructeur n'est donc qu'un defaut.
      this.applySavedMix();
      this.reverbSend = connectReverbSend(this.masterGain, this.reverbMix);
      // Meme garantie qu'avant : on n'initialise pas tant que la reponse
      // impulsionnelle n'est pas generee. Elle est desormais partagee.
      await whenReverbReady();

      // Install gesture unlock listeners so audio will be allowed on first tap
      this.setupGestureUnlock();

      this.initialized = true;

    } catch (error) {
      console.error('[MultiNotePlayer] Failed to initialize:', error);
      throw error;
    }
  }

  /**
   * Preload all 36 note WAV files
   * @param {Function} onProgress - Callback with (progress%, completed, total)
   */
  async preloadAllNotes(onProgress = null) {
    // Deja en cours : on rend la promesse en vol au lieu de rendre la main tout
    // de suite. L'effet de montage d'App.js part deux fois en developpement
    // (StrictMode), et le second appel declarait alors l'audio pret alors que le
    // premier decodait encore.
    if (this.isPreloading) return this._preloading;

    this.isPreloading = true;
    this._preloading = this._runPreloadAllNotes(onProgress);
    return this._preloading;
  }

  async _runPreloadAllNotes(onProgress) {
    if (!this.initialized) {
      await this.initialize();
    }

    const notes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    const octaves = [2, 3, 4];
    const total = notes.length * octaves.length;
    let loaded = 0;

    try {
      for (const octave of octaves) {
        for (const note of notes) {
          await this.loadNote(note, octave);
          loaded++;

          if (onProgress) {
            onProgress((loaded / total) * 100, loaded, total);
          }
        }
      }

      this.isPreloading = false;

    } catch (error) {
      this.isPreloading = false;
      console.error('[MultiNotePlayer] Failed to preload notes:', error);
      throw error;
    }
  }

  /**
   * Precharge d'abord les notes que la gamme affichee peut declencher, le reste
   * ensuite et sans bloquer. Mesure sur Galaxy A50 : les 36 loops mettent 10,7 s
   * a se decoder, pendant lesquelles aucune note ne sort. La gamme courante n'en
   * utilise qu'une vingtaine, donc l'attente avant la premiere note tombe d'autant.
   *
   * Android uniquement (App.js garde preloadAllNotes sur iOS et sur le web) :
   * la difference ne se voit que sur un appareil ou le decodage coute cher.
   *
   * @param {string[]} scaleNoteNames - notes de la gamme, orthographe musicale
   * @param {Function} onProgress - (progress%, completed, total) sur le lot prioritaire
   */
  async preloadScaleFirst(scaleNoteNames = [], onProgress = null) {
    // Meme verrou que preloadAllNotes : deux passes concurrentes se marcheraient
    // dessus et fausseraient la mesure de recordPreloadTiming.
    if (this.isPreloading) return this._preloading;

    this.isPreloading = true;
    this._preloading = this._runPreloadScaleFirst(scaleNoteNames, onProgress)
      .finally(() => { this.isPreloading = false; });
    return this._preloading;
  }

  async _runPreloadScaleFirst(scaleNoteNames, onProgress) {
    if (!this.initialized) {
      await this.initialize();
    }

    const wanted = [];
    const seen = new Set();
    (scaleNoteNames || []).forEach((name) => {
      const file = toFileNoteName(name);
      if (file && !seen.has(file)) {
        seen.add(file);
        wanted.push(file);
      }
    });

    // Gamme illisible ou vide : on retombe sur les 12 notes, donc sur l'ancien
    // comportement, plutot que de laisser le joueur avec des buffers manquants.
    const priority = wanted.length ? wanted : NOTE_NAMES;
    const rest = NOTE_NAMES.filter((n) => !priority.includes(n));

    const total = priority.length * NOTE_OCTAVES.length;
    let loaded = 0;
    const startedAt = Date.now();
    for (const octave of NOTE_OCTAVES) {
      for (const note of priority) {
        await this.loadNote(note, octave);
        loaded++;
        if (onProgress) onProgress((loaded / total) * 100, loaded, total);
      }
    }

    // Le temps mis a decoder ce lot sert de mesure de puissance : c'est lui qui
    // decide si l'appareil peut tenir la superposition nappe + piano.
    recordPreloadTiming(Date.now() - startedAt, loaded);

    this.preloadInBackground(rest);
  }

  /**
   * Termine le chargement des notes hors gamme, une par une et en laissant la
   * main entre chaque : un decodage FLAC monopolise le thread, et l'enchainer
   * sans pause revient a refaire le blocage qu'on vient de supprimer.
   */
  preloadInBackground(noteNames) {
    const queue = [];
    NOTE_OCTAVES.forEach((octave) => {
      (noteNames || []).forEach((note) => queue.push({ note, octave }));
    });
    if (!queue.length) return;

    const next = () => {
      const item = queue.shift();
      if (!item) return;
      this.loadNote(item.note, item.octave)
        .catch(() => {})
        .then(() => setTimeout(next, 60));
    };
    setTimeout(next, 300);
  }

  /**
   * Load a single note loop (FLAC, pre-cut by scripts/make-note-loops.py)
   */
  async loadNote(note, octave) {
    const key = `${note}${octave}`;

    if (this.noteBuffers.has(key)) {
      return; // Already loaded
    }

    try {
      // Build the file path
      let url;

      if (Capacitor.isNativePlatform()) {
        // On iOS/Android, files in public folder are served at the webview root
        // The build process copies them to ios/App/App/public/
        url = `audio/notes/${encodeURIComponent(note)}${octave}.flac`;
      } else {
        // On web, use absolute path
        url = `${process.env.PUBLIC_URL}/audio/notes/${encodeURIComponent(note)}${octave}.flac`;
      }

      // iOS has issues with fetch() for local files, use XMLHttpRequest
      const arrayBuffer = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        xhr.responseType = 'arraybuffer';

        xhr.onload = () => {
          if (xhr.status === 200 || xhr.status === 0) { // 0 for local files
            resolve(xhr.response);
          } else {
            reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
          }
        };

        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send();
      });
      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);

      this.noteBuffers.set(key, audioBuffer);

    } catch (error) {
      console.error(`[MultiNotePlayer] Failed to load ${key}:`, error);
      throw error;
    }
  }

  /**
   * Play a chord by mixing individual note WAV files
   * @param {string} rootNote - Root note (e.g., 'C', 'D#')
   * @param {string} quality - Chord quality (e.g., 'maj', 'min', 'dim')
   * @param {number} time - Optional start time for scheduled playback (in AudioContext time)
   */
  async playChord(rootNote, quality, time = null, scaleRoot = null, explicitNotes = null, drone = true) {
    if (!this.initialized) {
      console.warn('[MultiNotePlayer] Not initialized');
      return;
    }

    // Ensure the context is unlocked via user gesture before playback
    if (!this.gestureUnlocked) {
      await this.unlock();
    }

    // Resume audio context if suspended (iOS requirement)
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }

    // Get chord notes from music theory (or use the exact voicing the caller
    // supplied, e.g. pentatonic chords stacked within the scale).
    const chordNotes = explicitNotes || getChordNotes(rootNote, quality);

    // Voice the chord: drone bass below + chord tones in the fixed window.
    const notesToPlay = voiceChord(chordNotes, rootNote, scaleRoot, { drone }).map(note => ({
      ...note, mixChannel: quality === 'drone' || note.role === 'drone' ? 'drone' : 'chords',
    }));

    // Calculate start time (sample-accurate)
    // Now we share Tone.js's AudioContext, so timing is perfectly synced
    const startTime = time !== null ? time : this.audioContext.currentTime;

    const fadeOutStart = Math.max(this.audioContext.currentTime + 0.001, startTime - this.crossfadeLead);
    const fadeOutTime = fadeOutStart + this.crossfadeDuration;

    // Voice leading: a note held by the previous chord and still wanted by the new
    // one keeps its own source. Re-triggering it would layer a second copy of the
    // same looping sample at a different phase, and the two would comb-filter each
    // other for the whole crossfade, hollowing out exactly the notes the chords
    // have in common. A real pad holds those notes through the change.
    const pending = notesToPlay.slice();
    const keptSources = [];
    const keptGains = [];
    const oldSources = [];
    const oldGains = [];

    this.currentGains.forEach((gainNode, i) => {
      const source = this.currentSources[i];
      const wantedIndex = pending.findIndex(({ key }) => key === gainNode._key);

      if (wantedIndex === -1) {
        // Note dropped by the new chord: exponential fade out sounds more natural
        gainNode.gain.setValueAtTime(gainNode.gain.value, fadeOutStart);
        gainNode.gain.exponentialRampToValueAtTime(0.001, fadeOutTime);
        oldSources.push(source);
        oldGains.push(gainNode);
        return;
      }

      // Note held through the change. Its degree can differ from one chord to the
      // next (a third becoming a fifth), so glide to the new degree gain instead
      // of jumping.
      const { role, mixChannel } = pending[wantedIndex];
      pending.splice(wantedIndex, 1);

      const target = this.degreeGains[role] !== undefined ? this.degreeGains[role] : 1.0;
      const from = gainNode._appliedGain !== undefined ? gainNode._appliedGain : gainNode.gain.value;
      if (gainNode._mixChannel !== mixChannel) {
        gainNode.disconnect();
        gainNode.connect(this.mixBuses[mixChannel].input);
      }
      gainNode._mixChannel = mixChannel;
      gainNode._role = role;
      if (Math.abs(target - from) > 0.0001) {
        gainNode.gain.cancelScheduledValues(fadeOutStart);
        gainNode.gain.setValueAtTime(Math.max(from, 0.0001), fadeOutStart);
        // Linear so a muted degree can reach true zero
        gainNode.gain.linearRampToValueAtTime(target, fadeOutTime);
      }
      gainNode._appliedGain = target;
      keptSources.push(source);
      keptGains.push(gainNode);
    });

    if (oldSources.length > 0) {
      // Wait for the fade to actually finish. startTime can sit ahead of the
      // clock (the Transport schedules chords in advance), so counting from now
      // would cut the outgoing notes mid-fade.
      const cleanupDelay = Math.max(0, fadeOutTime - this.audioContext.currentTime) * 1000 + 100;
      setTimeout(() => {
        oldSources.forEach(source => {
          try {
            source.stop();
          } catch (e) {
            // Already stopped
          }
        });
        oldGains.forEach(gain => {
          gain.disconnect();
        });
      }, cleanupDelay);
    }

    // Keep the held voices; only the genuinely new notes are triggered below.
    this.currentSources = keptSources;
    this.currentGains = keptGains;

    const notesToTrigger = pending;

    // Load any missing notes on-demand (handles race with preloading)
    const missingNotes = notesToTrigger.filter(({ key }) => !this.noteBuffers.has(key));
    if (missingNotes.length > 0) {
      // Should never fire once preloading is done: it means a blocking
      // XHR + decode right before the chord sounds. Keep it visible.
      console.warn('[MultiNotePlayer] On-demand load during playChord:', missingNotes.map(n => n.key).join(', '));
      await Promise.all(missingNotes.map(({ key }) => {
        const note = key.replace(/\d+$/, '');
        const octave = parseInt(key.match(/\d+$/)[0]);
        return this.loadNote(note, octave);
      }));
    }

    // Start the new notes with fade-in
    notesToTrigger.forEach(({ key: noteKey, role }) => {
      const buffer = this.noteBuffers.get(noteKey);

      if (!buffer) {
        console.warn(`[MultiNotePlayer] Note ${noteKey} not loaded`);
        return;
      }

      // Create buffer source for this note
      const source = this.audioContext.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      // The FLAC files are pre-cut loops: the whole buffer is the loop region,
      // its end already crossfaded onto its start (scripts/make-note-loops.py).
      // Anything narrower would cut inside the crossfade and click again.
      source.loopStart = 0;
      source.loopEnd = buffer.duration;

      // Create individual gain node for this note
      const gainNode = this.audioContext.createGain();

      // Per-degree gain (drone is boosted by default for presence)
      const noteGain = this.degreeGains[role] !== undefined ? this.degreeGains[role] : 1.0;
      // Track role + applied gain so live fader moves can ramp from the right value.
      // _key identifies the pitch so the next chord can tell which voices to hold.
      gainNode._role = role;
      gainNode._appliedGain = noteGain;
      gainNode._key = noteKey;

      // Start from silence and fade in (floor target so a muted degree can't crash the exponential ramp)
      const fadeTarget = Math.max(noteGain, 0.0001);
      gainNode.gain.setValueAtTime(0.0001, startTime);
      gainNode.gain.exponentialRampToValueAtTime(fadeTarget, startTime + this.fadeInDuration);

      // Connect: source -> gainNode -> masterGain
      source.connect(gainNode);
      gainNode._mixChannel = quality === 'drone' || role === 'drone' ? 'drone' : 'chords';
      gainNode.connect(this.mixBuses[gainNode._mixChannel].input);

      // The baked-in swell that used to force a 1.5s start offset was dropped
      // when the loops were cut, so the voice is already at full level at 0.
      source.start(startTime);

      this.currentSources.push(source);
      this.currentGains.push(gainNode);
    });
  }

  /**
   * Stop all currently playing notes with fade out
   * @param {number} fadeOutDuration - Optional fade out time in seconds (default: crossfadeDuration)
   */
  stopAll(fadeOutDuration = null) {
    if (!this.audioContext) return;

    const fadeDuration = fadeOutDuration !== null ? fadeOutDuration : this.crossfadeDuration;
    const now = this.audioContext.currentTime;
    const stopTime = now + fadeDuration;

    // Fade out all current gains
    this.currentGains.forEach(gainNode => {
      try {
        gainNode.gain.setValueAtTime(gainNode.gain.value, now);
        gainNode.gain.exponentialRampToValueAtTime(0.001, stopTime);
      } catch (e) {
        // Ignore errors
      }
    });

    // Stop sources after fade out
    const sourcesToStop = this.currentSources;
    const gainsToDisconnect = this.currentGains;

    setTimeout(() => {
      sourcesToStop.forEach(source => {
        try {
          source.stop();
        } catch (e) {
          // Already stopped
        }
      });
      gainsToDisconnect.forEach(gain => {
        try {
          gain.disconnect();
        } catch (e) {
          // Already disconnected
        }
      });
    }, fadeDuration * 1000 + 100);

    this.currentSources = [];
    this.currentGains = [];
  }

  /**
   * Set master volume
   * @param {number} db - Volume in decibels (-60 to 0)
   */
  setVolume(db) {
    this.volume = db;

    if (this.masterGain) {
      // Convert dB to linear gain: gain = 10^(dB/20)
      const gain = Math.pow(10, db / 20);
      this.masterGain.gain.value = gain;
    }
  }

  /**
   * Set the gain for a chord degree ('drone', 'root', 'third', 'fifth', 'seventh').
   * Applies live to any sustaining notes of that degree, and to all future chords.
   * @param {string} degree - Degree key
   * @param {number} gain - Linear gain (0 = muted)
   */
  setDegreeVolume(degree, gain) {
    if (this.degreeGains[degree] === undefined) return;
    this.degreeGains[degree] = gain;

    if (!this.audioContext) return;
    const now = this.audioContext.currentTime;
    this.currentGains.forEach(gainNode => {
      if (gainNode._role !== degree) return;
      const from = gainNode._appliedGain !== undefined ? gainNode._appliedGain : gainNode.gain.value;
      gainNode.gain.cancelScheduledValues(now);
      gainNode.gain.setValueAtTime(Math.max(from, 0.0001), now);
      // Linear ramp can reach true zero, so mute works while sustaining
      gainNode.gain.linearRampToValueAtTime(gain, now + 0.05);
      gainNode._appliedGain = gain;
    });
  }

  /**
   * Enable/disable high octave (C4, D4, etc.)
   * @param {boolean} enabled
   */
  setPlayHighOctave(enabled) {
    this.playHighOctave = enabled;
  }

  /**
   * Set reverb wet mix
   * @param {number} mix - Wet mix amount (0-1)
   */
  setReverbMix(mix) {
    this.reverbMix = mix;
    // Le reglage utilisateur pilote desormais le gain d'envoi de la nappe, pas
    // un `wet` : meme dosage, meme courbe (cf reverbBus).
    if (this.reverbSend) this.reverbSend.setWet(mix);
  }

  /**
   * Set reverb decay time
   * @param {number} decay - Decay time in seconds
   */
  setReverbDecay(decay) {
    this.reverbDecay = decay;
    // La duree est commune a toutes les sources depuis le passage en bus
    // d'envoi : ce reglage agit donc sur toute l'app, plus seulement sur la
    // nappe. Debounce conserve, regenerer la reponse impulsionnelle coute cher.
    clearTimeout(this._reverbDecayTimeout);
    this._reverbDecayTimeout = setTimeout(() => setSharedReverbDecay(decay), 400);
  }

  /**
   * Clean up resources
   */
  dispose() {
    this.stopAll();
    this.noteBuffers.clear();
    Object.values(this.mixBuses || {}).forEach(bus => bus.dispose());
    // Les deux gains de l'envoi, jamais la reverb ni le bus stereo partages.
    if (this.reverbSend) {
      this.reverbSend.dispose();
      this.reverbSend = null;
    }

    // Do NOT close this.audioContext — it's Tone.getContext().rawContext (shared).
    // Closing it would kill all audio globally (metronome, synth, etc.).

    this.initialized = false;
  }

  /**
   * Get status information
   * @returns {Object} Status object
   */
  getStatus() {
    return {
      initialized: this.initialized,
      isPreloading: this.isPreloading,
      loadedNotes: this.noteBuffers.size,
      totalNotes: 36,
      playingSources: this.currentSources.length,
      audioContextState: this.audioContext ? this.audioContext.state : 'not created',
      sampleRate: this.audioContext ? this.audioContext.sampleRate : null,
      volume: this.volume,
      playHighOctave: this.playHighOctave,
      gestureUnlocked: this.gestureUnlocked
    };
  }
}

// Export singleton instance
const multiNotePlayer = new MultiNotePlayer();
export default multiNotePlayer;
